/*
 * Accounts and what they can reach. Super admin only.
 *
 * Every control here can hand somebody else the keys, so the screen is written to make
 * the consequence visible rather than to make the action quick: the role picker names
 * what each role can do, a generated password is shown once and says so, and an account
 * that cannot sign in at all is called out rather than left to be inferred from a blank.
 *
 * Staff accounts only, and the role picker offers only staff roles. Moving somebody to
 * `participant` would drop them out of the one screen that could undo it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { KeyRound, Plus, ShieldAlert, TriangleAlert, UserPlus, X } from 'lucide-react'
import { api } from '../lib/api'
import type { AdminUser, AdminUsersResponse } from '../lib/types'
import { useAuthed } from '../lib/session'
import { Masthead } from '../ui/Chrome'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'
import { Sheet } from '../ui/Sheet'
import { Notice, Skeleton } from '../ui/Feedback'

/*
 * What each role actually reaches, in the same words the gates use. A picker that says
 * only "admin" asks somebody to remember a permission model at the moment they are
 * changing it.
 */
const ROLE_CHOICES = [
  { id: 1, name: 'Super admin', blurb: 'Everything, including this screen' },
  { id: 2, name: 'Organiser', blurb: 'Settings, rooms, attendance reporting' },
  { id: 3, name: 'Moderator', blurb: 'No access yet — defined but unused' },
  { id: 4, name: 'Venue display', blurb: 'Show a code, scan passes, extend a session' },
]

export function UserAdminRoute() {
  const session = useAuthed()
  const [data, setData] = useState<AdminUsersResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /* A generated password, held in memory only, until it is dismissed. */
  const [issued, setIssued] = useState<{ user_name: string; password: string } | null>(null)
  const [adding, setAdding] = useState(false)

  const load = useCallback(() => {
    setFailed(false)
    api.adminUsers(session.email, session.at)
      .then(setData)
      .catch(() => setFailed(true))
  }, [session.email, session.at])

  useEffect(load, [load])

  const venues = data?.venues ?? []
  const grouped = useMemo(() => {
    const users = data?.users ?? []
    return ROLE_CHOICES
      .map((role) => ({ role, users: users.filter((u) => u.id_role === role.id) }))
      .filter((g) => g.users.length > 0)
  }, [data])

  async function run(key: string, work: () => Promise<unknown>) {
    setBusy(key)
    setError(null)
    try {
      await work()
      load()
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'That did not save.')
    } finally {
      setBusy(null)
    }
  }

  /* The caller's own row, which the server refuses to edit — so it is not offered. */
  const isSelf = (user: AdminUser) =>
    user.user_name.toLowerCase() === session.email.toLowerCase()

  /*
   * Assignments whose venue belongs to a different event, or to no venue row at all.
   * The picker above only draws this event's rooms, so without this they would be
   * invisible while still sitting in qr_venue.
   */
  const thisEvent = new Set(venues.map((v) => v.id))
  const outsideEvent = (user: AdminUser) => user.venues.filter((v) => !thisEvent.has(v.id))

  return (
    <main className="page useradmin-page">
      <Masthead kicker="Super admin" title="Accounts" />

      {data === null && !failed ? <Skeleton height="16rem" /> : null}

      {failed ? (
        <Notice
          icon={<ShieldAlert size={22} strokeWidth={1.3} />}
          title="Could not load accounts"
          body="The server did not answer, or this account is not a super admin."
          action={{ label: 'Try again', onClick: load }}
        />
      ) : null}

      {data ? (
        <>
          <div className="useradmin-actions">
            <Button
              variant="primary"
              size="sm"
              icon={<UserPlus size={16} strokeWidth={1.6} />}
              onClick={() => setAdding(true)}
            >
              Add account
            </Button>
          </div>

          {error ? <p className="field__error useradmin-error" role="alert">{error}</p> : null}

          {grouped.map(({ role, users }) => (
            <section className="useradmin-group" key={role.id}>
              <div className="useradmin-group__head">
                <h2 className="useradmin-group__title">{role.name}</h2>
                <span className="kicker">{role.blurb}</span>
              </div>

              {users.map((user) => (
                <article className="useradmin-card" key={user.id}>
                  <div className="useradmin-card__id">
                    <p className="useradmin-card__name">{user.full_name || user.user_name}</p>
                    <p className="useradmin-card__email">{user.user_name}</p>
                    <p className="useradmin-card__meta">
                      id {user.id}
                      {user.last_login ? ` · last signed in ${user.last_login}` : ' · never signed in'}
                    </p>
                    {/*
                      * An account with no password cannot use /altLogin at all. That is
                      * true of `supadmin` today, and it is the kind of thing that is
                      * discovered at the worst possible moment.
                      */}
                    {!user.has_password ? (
                      <p className="useradmin-card__warn">
                        <TriangleAlert size={14} strokeWidth={1.8} />
                        No password set — this account cannot sign in
                      </p>
                    ) : null}
                  </div>

                  <label className="useradmin-card__role">
                    <span className="kicker">Role</span>
                    <select
                      value={user.id_role}
                      disabled={isSelf(user) || busy === `role-${user.id}`}
                      onChange={(e) => void run(`role-${user.id}`, () =>
                        api.adminSetRole(session.email, session.at, user.id, Number(e.target.value)))}
                    >
                      {ROLE_CHOICES.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    {isSelf(user) ? (
                      <span className="useradmin-card__self">Your own account</span>
                    ) : null}
                  </label>

                  <div className="useradmin-card__venues">
                    <span className="kicker">Rooms at this event</span>
                    {/*
                      * qr_venue is the access boundary for venue codes and for
                      * /slot/extend, so this is a permission control wearing the
                      * clothes of a preference. An account with none sees no codes.
                      *
                      * Scoped to the current event. The `venue` table spans six years -
                      * 29 rows, three of them called "Cafe Anjung Sendayan, ILD UiTM
                      * Bandar Enstek" and one with no name - so an unscoped picker was
                      * a wall of near-identical chips, most of them for rooms that no
                      * longer exist.
                      */}
                    <div className="useradmin-chips">
                      {venues.map((venue) => {
                        const on = user.venues.some((v) => v.id === venue.id)
                        return (
                          <button
                            key={venue.id}
                            type="button"
                            className="useradmin-chip"
                            data-on={on || undefined}
                            disabled={busy === `venues-${user.id}`}
                            onClick={() => void run(`venues-${user.id}`, () => {
                              const next = on
                                ? user.venues.filter((v) => v.id !== venue.id).map((v) => v.id)
                                : [...user.venues.map((v) => v.id), venue.id]
                              return api.adminSetVenues(session.email, session.at, user.id, next)
                            })}
                          >
                            {venue.name || `Venue ${venue.id}`}
                          </button>
                        )
                      })}
                    </div>

                    {/*
                      * Assignments pointing at another event's rooms.
                      *
                      * Shown, not hidden: every live staff account is currently assigned
                      * to venue 19 - "Hotel Granada", event 5 - which grants nothing at
                      * this event, and a picker that only drew this event's rooms would
                      * have made that invisible while quietly preserving it. They are
                      * removable here, and nowhere else.
                      */}
                    {outsideEvent(user).length > 0 ? (
                      <div className="useradmin-elsewhere">
                        <p className="useradmin-elsewhere__note">
                          <TriangleAlert size={14} strokeWidth={1.8} />
                          Grants nothing here — rooms from another event
                        </p>
                        <div className="useradmin-chips">
                          {outsideEvent(user).map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              className="useradmin-chip useradmin-chip--stale"
                              disabled={busy === `venues-${user.id}`}
                              title="Remove this assignment"
                              onClick={() => void run(`venues-${user.id}`, () =>
                                api.adminSetVenues(session.email, session.at, user.id,
                                  user.venues.filter((x) => x.id !== v.id).map((x) => x.id)))}
                            >
                              {v.name || `Venue ${v.id}`}
                              <X size={12} strokeWidth={2.2} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {user.venues.length === 0 ? (
                      <p className="useradmin-card__meta">None — sees no venue codes</p>
                    ) : null}
                  </div>

                  <div className="useradmin-card__foot">
                    <Button
                      variant="outline"
                      size="sm"
                      busy={busy === `pw-${user.id}`}
                      icon={<KeyRound size={15} strokeWidth={1.6} />}
                      onClick={() => void run(`pw-${user.id}`, async () => {
                        const res = await api.adminResetPassword(session.email, session.at, user.id)
                        setIssued({ user_name: res.user_name, password: res.password })
                      })}
                    >
                      {user.has_password ? 'Reset password' : 'Set a password'}
                    </Button>
                  </div>
                </article>
              ))}
            </section>
          ))}

          {/*
            * Orphaned venue assignments. qr_venue keys on an email, so a row survives
            * the account being deleted - and it is a live grant pointing at nobody.
            * Shown because the table is now a permission surface, not a preference.
            */}
          {data.orphan_venue_rows.length > 0 ? (
            <section className="useradmin-orphans">
              <h2 className="useradmin-group__title">Venue assignments with no account</h2>
              <p className="useradmin-card__meta">
                Left behind when an account was removed. Harmless while no account uses
                the address, but these are grants — clear them in the database.
              </p>
              <ul>
                {data.orphan_venue_rows.map((row) => (
                  <li key={`${row.user_name}-${row.id_venue}`}>
                    <strong>{row.user_name}</strong> → {row.venue_name || `venue ${row.id_venue}`}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}

      {/* Shown once. Nothing stores the plaintext, so there is no second chance. */}
      <Sheet open={Boolean(issued)} onClose={() => setIssued(null)} title="New password">
        <h2 className="detail__title">New password</h2>
        <p className="muted useradmin-once">
          For <strong>{issued?.user_name}</strong>. This is the only time it is shown —
          nothing stores it, and it cannot be looked up again.
        </p>
        <p className="useradmin-password tnum">{issued?.password}</p>
        <Button variant="primary" onClick={() => setIssued(null)}>I have saved it</Button>
      </Sheet>

      <AddAccountSheet
        open={adding}
        venues={venues}
        onClose={() => setAdding(false)}
        onCreated={(res) => {
          setAdding(false)
          setIssued({ user_name: res.user_name, password: res.password })
          load()
        }}
      />
    </main>
  )
}

function AddAccountSheet({
  open, venues, onClose, onCreated,
}: {
  open: boolean
  venues: Array<{ id: number; name: string }>
  onClose: () => void
  onCreated: (res: { user_name: string; password: string }) => void
}) {
  const session = useAuthed()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState(4)
  const [picked, setPicked] = useState<number[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const res = await api.adminCreateUser(session.email, session.at, {
        user_name: email.trim(), full_name: name.trim(), id_role: role, venue_ids: picked,
      })
      setEmail(''); setName(''); setRole(4); setPicked([])
      onCreated(res)
    } catch (e) {
      /*
       * The server's own words. It refuses an address that already has an account and
       * explains why - repeating that here as "could not create" would throw away the
       * only part of the message that tells you what to do instead.
       */
      setError(e instanceof Error && e.message ? e.message : 'Could not create the account.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Add a staff account">
      <div className="useradmin-sheet-head">
        <h2 className="detail__title">Add a staff account</h2>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>

      <Field
        label="Email address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="name@uitm.edu.my"
        hint="Must not already have an account — including as a delegate."
      />
      <Field
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Displayed on the account"
      />

      <label className="useradmin-card__role">
        <span className="kicker">Role</span>
        <select value={role} onChange={(e) => setRole(Number(e.target.value))}>
          {ROLE_CHOICES.map((r) => (
            <option key={r.id} value={r.id}>{r.name} — {r.blurb}</option>
          ))}
        </select>
      </label>

      <div className="useradmin-card__venues">
        <span className="kicker">Venues</span>
        <div className="useradmin-chips">
          {venues.map((v) => (
            <button
              key={v.id}
              type="button"
              className="useradmin-chip"
              data-on={picked.includes(v.id) || undefined}
              onClick={() => setPicked((cur) =>
                cur.includes(v.id) ? cur.filter((x) => x !== v.id) : [...cur, v.id])}
            >
              {v.name}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="field__error" role="alert">{error}</p> : null}

      <Button
        variant="primary"
        busy={busy}
        icon={<Plus size={16} strokeWidth={1.7} />}
        disabled={!email.trim() || !name.trim()}
        onClick={() => void submit()}
      >
        Create account
      </Button>
    </Sheet>
  )
}
